import '@/editor/editorTheme.css'
import { SidebarProvider } from '@/layout/contexts/SidebarContext'
import { ApolloProvider } from '@apollo/client'
import {
  Box,
  Button,
  ChakraProvider,
  ColorModeScript,
  Heading,
  Text,
  VStack,
} from '@chakra-ui/react'
import '@rolebase/editor/src/react/styles.css'
import { StoreProvider } from 'easy-peasy'
import React, { Component, type ErrorInfo, type ReactNode } from 'react'
import { I18nextProvider } from 'react-i18next'
import { BrowserRouter as Router } from 'react-router'
import { createApolloClient, resetApolloClient } from './apolloClient'
import i18n from './i18n'
import { nhost } from './nhost'
import AppRoute from './routes/AppRoute'
import { store } from './store'
import theme from './theme'
import { AuthProvider } from '@/user/contexts/AuthProvider'

const apolloClient = createApolloClient({
  // User admin role id "admin" is in URL query params
  'x-hasura-role': /[?&]admin(&|$)/.test(window.location.search)
    ? 'admin'
    : 'user',
})

// Catch unhandled async errors that React error boundary can't see
window.addEventListener('unhandledrejection', (event) => {
  console.error('[App] Unhandled promise rejection:', event.reason)
})

window.addEventListener('error', (event) => {
  console.error('[App] Global error:', event.error || event.message)
})

interface ErrorBoundaryState {
  hasError: boolean
}

class AppErrorBoundary extends Component<
  { children: ReactNode },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[App] Uncaught error:', error, errorInfo)
  }

  handleReset = () => {
    nhost.clearSession()
    resetApolloClient().catch(() => {})
    nhost.auth.signOut({}).catch(() => {})
    window.location.href = '/'
  }

  render() {
    if (this.state.hasError) {
      return (
        <ChakraProvider theme={theme}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            minH="100vh"
          >
            <VStack spacing={4}>
              <Heading size="md">Something went wrong</Heading>
              <Text color="gray.500">
                The application encountered an unexpected error.
              </Text>
              <Button colorScheme="blue" onClick={this.handleReset}>
                Sign out and restart
              </Button>
            </VStack>
          </Box>
        </ChakraProvider>
      )
    }
    return this.props.children
  }
}

export default function App() {
  return (
    <AppErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <ChakraProvider theme={theme}>
          <ColorModeScript initialColorMode={theme.config.initialColorMode} />
          <ApolloProvider client={apolloClient}>
            <AuthProvider>
              <SidebarProvider>
                <StoreProvider store={store}>
                  <Router>
                    <AppRoute />
                  </Router>
                </StoreProvider>
              </SidebarProvider>
            </AuthProvider>
          </ApolloProvider>
        </ChakraProvider>
      </I18nextProvider>
    </AppErrorBoundary>
  )
}
